<script setup lang="ts">
import type { FiscalShiftAction } from '@/entities/fiscalization';

import { fiscalShiftActionLabel } from '@/entities/fiscalization';
import { AppButton } from '@/shared/ui';

import type { UseFiscalShiftStatusReturn } from '../model/use-fiscal-shift-status';

import {
  fiscalShiftToneClasses as toneClasses,
  useFiscalShiftPresentation,
} from '../model/use-fiscal-shift-presentation';

/** Состояние смены и её действие: встраиваемый блок плашки локации. */
const props = defineProps<{ status: UseFiscalShiftStatusReturn; locationId: string }>();
const emit = defineEmits<{ action: [action: Exclude<FiscalShiftAction, null>] }>();

const { cancelClosing, isActionPending, isLoading, kktConcern, shiftState } = props.status;
const { cardClasses, dismissReminder, dotClasses, reminder, visiblePresentation } = useFiscalShiftPresentation(
  props.status,
  () => props.locationId,
);

const beginAction = (action: Exclude<FiscalShiftAction, null>): void => {
  emit('action', action);
};

const abortClosing = async (): Promise<void> => {
  await cancelClosing();
};
</script>

<template>
  <div
    role="group"
    aria-label="Состояние кассовой смены"
    class="rounded-xl p-3"
    :class="cardClasses"
  >
    <div class="flex items-start gap-3">
      <span
        class="mt-1 h-3 w-3 shrink-0 rounded-full"
        :class="dotClasses"
        aria-hidden="true"
      />
      <div class="min-w-0">
        <p class="font-semibold">{{ visiblePresentation.title }}</p>
        <p class="mt-1 text-sm leading-5 text-(--text-secondary)">
          {{ visiblePresentation.description }}
        </p>
      </div>
    </div>
    <div
      v-if="kktConcern !== null"
      class="mt-3 flex items-start gap-3 border-t border-(--border) pt-3"
    >
      <span
        class="mt-1 h-3 w-3 shrink-0 rounded-full"
        :class="toneClasses[kktConcern.tone].dot"
        aria-hidden="true"
      />
      <div class="min-w-0">
        <p class="font-semibold">{{ kktConcern.title }}</p>
        <p class="mt-1 text-sm leading-5 text-(--text-secondary)">
          {{ kktConcern.description }}
        </p>
      </div>
    </div>
    <div
      v-if="reminder?.dismissible || shiftState?.action"
      class="mt-3 flex flex-col gap-2"
    >
      <AppButton
        v-if="reminder?.dismissible"
        class="min-h-11"
        outline
        @click="dismissReminder"
      >
        Понятно
      </AppButton>
      <template v-else-if="shiftState?.action">
        <!-- Отмена закрытия иначе видна только внутри диалога закрытия — из чипа её не найти. -->
        <AppButton
          v-if="shiftState.status === 'shift_closing'"
          class="min-h-11"
          outline
          :loading="isActionPending"
          :disabled="isLoading"
          @click="abortClosing"
        >
          Вернуть смену в работу
        </AppButton>
        <AppButton
          v-if="shiftState.status === 'shift_kkt_desync'"
          class="min-h-11"
          outline
          :loading="isActionPending"
          :disabled="isLoading"
          @click="beginAction('reopen')"
        >
          Продолжить работу
        </AppButton>
        <AppButton
          class="min-h-11"
          :loading="isActionPending"
          :disabled="isLoading"
          @click="beginAction(shiftState.action)"
        >
          {{ fiscalShiftActionLabel[shiftState.action] }}
        </AppButton>
      </template>
    </div>
  </div>
</template>
